'use client';

import { Button } from '@curology/ui-components-web/react/Button';
import { TextInput } from '@curology/ui-components-web/react/TextInput';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React, { useEffect, useState } from 'react';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { centsToDollarsAndCents } from '@/helpers/productSelectionAndCheckout/helpers';
import {
  claimPromotion,
  removePromotionClaim,
} from '@/services/promotionService/actions';
import { type Exception } from '@/types/productSelectionAndCheckout/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

import { usePromotionClaim } from '../hooks/usePromotionClaim';

export const PromotionCodeInputAndDisplay = () => {
  useEffect(() => {
    trackEvent(AnalyticsEvent.PROMO_CODE_VIEWED);
  }, []);

  const { promotionClaim, setPromotionClaim } = usePromotionClaim();

  const [promotionCode, setPromotionCode] = useState(
    promotionClaim?.code || '',
  );
  const [promotionCodeInputError, setPromotionCodeInputError] = useState<
    string | undefined
  >();
  const [showPromotionCodeInput, setShowPromotionCodeInput] = useState(false);
  const { treatmentCategory, brand } = useFlowContext();

  if (promotionClaim) {
    return (
      <div className="cur-flex cur-justify-between cur-mt-2">
        <Typography>
          <span className="cur-font-bold">{promotionClaim.code}</span> applied
          (-
          {centsToDollarsAndCents(promotionClaim.amount)})
        </Typography>
        <Button
          buttonType="text"
          data-sentry-id="Remove promo claim"
          flush
          onClick={async () => {
            try {
              const success = await removePromotionClaim(
                promotionClaim.promotionCodeId,
                brand,
              );

              if (success) {
                setPromotionClaim(undefined);
              } else {
                setPromotionCodeInputError('Failed to remove promotion claim');
              }
            } catch (exception) {
              setPromotionCodeInputError('Failed to remove promotion claim');
            }
          }}
          size="sm"
        >
          Remove
        </Button>
      </div>
    );
  }

  return (
    <div className="cur-mt-2">
      <Button
        buttonType="text"
        data-sentry-id="Show promo code input"
        flush
        onClick={() => {
          const newShowPromotionCodeInput = !showPromotionCodeInput;

          setShowPromotionCodeInput(newShowPromotionCodeInput);

          trackEvent(AnalyticsEvent.PROMO_CODE_TOGGLED, {
            action: newShowPromotionCodeInput ? 'open' : 'close',
          });
        }}
        size="sm"
      >
        Have a promo code?
      </Button>
      {showPromotionCodeInput && (
        <div className="cur-flex cur-mt-4">
          <TextInput
            className="cur-flex-1"
            helperText={promotionCodeInputError}
            id="promotionCode"
            onChange={(_event, value) => setPromotionCode(value as string)}
            placeholder="Promo code"
            state={promotionCodeInputError ? 'critical' : ''}
            value={promotionCode}
          />
          <Button
            buttonType="secondary"
            className="cur-ml-4"
            data-sentry-id="Apply promo claim"
            onClick={async () => {
              try {
                trackEvent(AnalyticsEvent.PROMO_CODE_ENTERED, {
                  code: promotionCode,
                });

                const promotionClaim = await claimPromotion(
                  promotionCode,
                  brand,
                  treatmentCategory,
                );

                if (promotionClaim) {
                  if (promotionClaim.promotionCode === promotionCode) {
                    setPromotionClaim({
                      amount: promotionClaim.amountInDollars * 100,
                      code: promotionClaim.promotionCode,
                      promotionCodeId: promotionClaim.promotionId,
                    });

                    setPromotionCodeInputError(undefined);
                    setShowPromotionCodeInput(false);

                    trackEvent(AnalyticsEvent.PROMO_CODE_MANUALLY_APPLIED, {
                      promoCode: promotionClaim.promotionCode.toUpperCase(),
                    });
                  }
                } else {
                  setPromotionCodeInputError('Invalid promotion code');
                }
              } catch (exception) {
                const {
                  response: {
                    data: { message },
                  },
                } = exception as Exception;

                setPromotionCodeInputError(message);
              }
            }}
          >
            Apply
          </Button>
        </div>
      )}
    </div>
  );
};
